import { saveAs } from 'file-saver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fetchWithAuth = async (url, options = {}) => {
  const config = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken) {
    config.headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const response = await fetch(url, config);
  return response;
};

export const api = {
  // Auth
  exchangeSession: async (sessionId) => {
    const res = await fetch(`${API}/auth/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    return res.json();
  },

  getMe: async () => {
    const res = await fetchWithAuth(`${API}/auth/me`);
    if (!res.ok) throw new Error('Not authenticated');
    return res.json();
  },

  logout: async () => {
    await fetchWithAuth(`${API}/auth/logout`, { method: 'POST' });
    localStorage.removeItem('session_token');
  },

  // Reports
  calculateReport: async (inputs) => {
    const res = await fetchWithAuth(`${API}/reports/calculate`, {
      method: 'POST',
      body: JSON.stringify(inputs),
    });
    if (res.status === 403) {
      const data = await res.json();
      throw new Error(data.detail?.message || 'Free report limit reached');
    }
    if (res.status === 422) {
      const data = await res.json();
      throw new Error(data.detail?.errors?.join(', ') || 'Validation failed');
    }
    return res.json();
  },

  unlockReport: async (reportId) => {
    const res = await fetchWithAuth(`${API}/reports/${reportId}/unlock`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to unlock');
    return res.json();
  },

  getReport: async (reportId) => {
    const res = await fetchWithAuth(`${API}/reports/${reportId}`);
    if (!res.ok) throw new Error('Report not found');
    return res.json();
  },

  listReports: async () => {
    const res = await fetchWithAuth(`${API}/reports`);
    if (!res.ok) return [];
    return res.json();
  },

  // Payments
  createPaymentOrder: async (plan, reportId) => {
    const res = await fetchWithAuth(`${API}/payments/create-order`, {
      method: 'POST',
      body: JSON.stringify({ plan, report_id: reportId }),
    });
    if (!res.ok) throw new Error('Failed to create order');
    return res.json();
  },

  verifyPayment: async (data) => {
    const res = await fetchWithAuth(`${API}/payments/verify`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Verification failed');
    return res.json();
  },

  // Scenarios
  createScenario: async (reportId, label, modifications) => {
    const res = await fetchWithAuth(`${API}/scenarios/create`, {
      method: 'POST',
      body: JSON.stringify({ report_id: reportId, label, modifications }),
    });
    if (!res.ok) throw new Error('Failed to create scenario');
    return res.json();
  },

  getScenarios: async (reportId) => {
    const res = await fetchWithAuth(`${API}/scenarios/${reportId}`);
    if (!res.ok) return [];
    return res.json();
  },

  // PDF Download — uses file-saver library for reliable cross-browser downloads
  downloadPdf: async (reportId) => {
    const sessionToken = localStorage.getItem('session_token');
    const url = `${API}/reports/${reportId}/pdf${sessionToken ? `?token=${encodeURIComponent(sessionToken)}` : ''}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    saveAs(blob, `feasibility_report_${reportId}.pdf`);
  },

  // Sensitivity Analysis
  getSensitivity: async (reportId) => {
    const res = await fetchWithAuth(`${API}/reports/${reportId}/sensitivity`);
    if (!res.ok) throw new Error('Sensitivity analysis failed');
    return res.json();
  },

  // Consultations
  createConsultation: async (data) => {
    const res = await fetchWithAuth(`${API}/consultations/create`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create consultation');
    return res.json();
  },

  listConsultations: async () => {
    const res = await fetchWithAuth(`${API}/consultations`);
    if (!res.ok) return [];
    return res.json();
  },

  updateConsultation: async (id, data) => {
    const res = await fetchWithAuth(`${API}/consultations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.json();
  },

  // Dashboard
  getDashboard: async () => {
    const res = await fetchWithAuth(`${API}/user/dashboard`);
    if (!res.ok) throw new Error('Failed to load dashboard');
    return res.json();
  },

  getCredits: async () => {
    const res = await fetchWithAuth(`${API}/user/credits`);
    if (!res.ok) return { credits: 0, plan: 'free' };
    return res.json();
  },
};

// Razorpay helper
export const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const initiatePayment = async (plan, reportId, user, onSuccess) => {
  const loaded = await loadRazorpay();
  if (!loaded) {
    alert('Payment service unavailable');
    return;
  }

  const order = await api.createPaymentOrder(plan, reportId);

  const options = {
    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
    amount: order.amount,
    currency: order.currency,
    name: 'Restaurant Feasibility Engine',
    description: `${plan.replace('_', ' ')} Plan`,
    order_id: order.order_id,
    handler: async (response) => {
      try {
        await api.verifyPayment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          plan: plan,
          report_id: reportId,
        });
        if (onSuccess) onSuccess();
      } catch (err) {
        alert('Payment verification failed');
      }
    },
    prefill: {
      name: user?.name || '',
      email: user?.email || '',
    },
    theme: {
      color: '#1D1D1F',
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.open();
};
